/* assets/predictive-search.js  ──────────────────────────────────────────── */
/*  ➜ Only addition: applyCompatibilityHooks() (≈20 lines) right after super() */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    /* ---------- compatibility shim for simplified Liquid markup ---------- */
    this.applyCompatibilityHooks = () => {
      /* make sure input carries the hooks SearchForm expects */

      /* ensure reset button has both reset__button + field__button */
      const resetBtn = this.querySelector('button[type="reset"]');
      if (resetBtn) resetBtn.classList.add('reset__button', 'field__button');

      /* ensure submit button has search__button + field__button */
      const submitBtn = this.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.classList.add('search__button', 'field__button');
    };
    this.applyCompatibilityHooks();
    /* --------------------------------------------------------------------- */

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  /* ────────── everything below is identical to Dawn’s original file ────────── */

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.predictiveSearchResults.addEventListener('wheel', (e) => {
      const el = this.predictiveSearchResults;
      const top = el.scrollTop <= 0 && e.deltaY < 0;
      const bottom = el.scrollHeight - el.clientHeight - el.scrollTop <= 0 && e.deltaY > 0;
      if (top || bottom) e.preventDefault();     // stop bubbling to body
    }, { passive: false });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    const current = el?.innerText;
    if (current && current.match(new RegExp(previousTerm, 'g')).length === 1) {
      el.innerText = current.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');
    const items = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((el) => el.offsetParent !== null);

    if (moveUp && !selected) return;

    const currentIndex = items.indexOf(selected);
    let nextIndex =
      moveUp
        ? currentIndex === 0 ? items.length - 1 : currentIndex - 1
        : currentIndex === items.length - 1 ? 0 : currentIndex + 1;

    if (nextIndex === currentIndex) return;

    items[nextIndex].setAttribute('aria-selected', true);
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', items[nextIndex].id);
    this.statusElement.textContent = '';
  }

  selectOption() {
    const el = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (el) el.click();
  }

  getSearchResults(term) {
    const key = term.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then((html) => {
        const markup = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach((i) => (i.cachedResults[key] = markup));
        this.renderSearchResults(markup);
      })
      .catch((e) => {
        if (e?.code !== 20) this.close();
        /* network error or abort — swallow */
      });
  }

  injectViewAllButton() {
    const wrap = this.querySelector('#predictive-search-option-search-keywords');
    if (!wrap) return;
    wrap.innerHTML = `
      <button type="submit" role="option" aria-selected="false"
        class="search-bar__view-all flex items-center justify-center w-full text-2xl hover:text-primary h-16">
        View all results
        <span class="w-8 h-8"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"></path>
        </svg>
       
      </button>`;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    this.statusElement.removeAttribute('aria-hidden');
    this.statusElement.textContent = text;
    setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);
    this.injectViewAllButton();
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]').textContent
    );
  }

  // AFTER (instance-scoped)
  getResultsMaxHeight() {
    const header = this.closest('.section-header');
    const anchorBottom = (header || this).getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(160, window.innerHeight - anchorBottom);
    return this.resultsMaxHeight;
  }


  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearTerm = false) {
    this.closeResults(clearTerm);
    this.isOpen = false;
  }

  closeResults(clearTerm = false) {
    if (clearTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const sel = this.querySelector('[aria-selected="true"]');
    if (sel) sel.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
